import React, { useState } from 'react';
import { Typography } from '@mui/material';

import { PropertyTextInput } from '@paragon/client-tools';
import { MaterialSelection } from '@paragon/contract-library';
import { Dialog } from '@paragon/popup-state';

import { SettingsOfType, settingsPresetsOfType, SettingsPresetType } from '../presets/presetTypes';

export function SavePresetDialog<T extends SettingsPresetType>(props: {
  settingsType: T;
  settings: SettingsOfType<T>;
  materialSelection?: MaterialSelection | null;
  onPresetsInvalidated(options?: { suppressSpinner?: boolean }): Promise<void>;
  onClose(): void;
}) {
  const [name, setName] = useState<string | null>(null);
  const [validating, setValidating] = useState(false);

  const [didSavingFail, setDidSavingFail] = useState(false);

  const metadata = settingsPresetsOfType(props.settingsType);

  return (
    <Dialog
      insistent
      title="Save As Preset"
      variant="design"
      onCancel={props.onClose}
      submitText="Save"
      contentSx={{ display: 'flex', flexDirection: 'column' }}
      onSubmit={() => {
        if (name == null || name.trim() === '') {
          setValidating(true);
          return;
        }

        if (metadata.hasMaterialSelection && props.materialSelection == null) {
          throw new Error('Invalid preset encountered -- missing material selection when required.');
        }

        return metadata
          .create(name, props.settings, metadata.hasMaterialSelection ? props.materialSelection! : undefined)
          .then(() => props.onPresetsInvalidated({ suppressSpinner: true }))
          .then(props.onClose)

          .catch((error) => {
            console.error(error);

            setDidSavingFail(true);
          });
      }}
      actionsNode={
        didSavingFail ? (
          <Typography color="red">Error saving preset. Try again or contact Paragon Support.</Typography>
        ) : null
      }
    >
      <Typography variant="body1">
        Creating a preset allows you to easily apply these exact settings in the future.
      </Typography>

      <PropertyTextInput
        property="Name"
        value={name ?? ''}
        error={validating && (name == null || name.trim() === '')}
        onFocus={() => setValidating(false)}
        submit={setName}
        InputProps={{ autoComplete: 'off' }}
      />
    </Dialog>
  );
}
